import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getConsignments,
  getConsignment,
  Consignment,
  ConsignmentFilters,
  PaginatedResponse,
} from "../../services/consignmentApi";

export interface ConsignmentsState {
  items: Consignment[];
  pagination: { current_page: number; per_page: number; total: number };
  loading: boolean;
  error: string | null;
  selected: Consignment | null;
  filters: ConsignmentFilters;
}

const initialState: ConsignmentsState = {
  items: [],
  pagination: { current_page: 1, per_page: 10, total: 0 },
  loading: false,
  error: null,
  selected: null,
  filters: {
    query: "",
    status: "",
    booking_type: "",
    customer_id: "",
    destination: "",
    page: 1,
    per_page: 10,
    first_mile_node_id: "",
    current_node_id: "",
    last_mile_node_id: "",
    created_from: "",
    created_to: "",
    updated_from: "",
    updated_to: "",
  },
};

export const fetchConsignments = createAsyncThunk<
  PaginatedResponse<Consignment>,
  void,
  { state: any }
>("consignments/fetch", async (_, thunkAPI) => {
  const { filters } = thunkAPI.getState().consignments as ConsignmentsState;
  console.log("Redux: fetchConsignments called with filters:", filters);
  const result = await getConsignments(filters);
  console.log("Redux: getConsignments returned:", result);
  return result;
});

export const fetchConsignment = createAsyncThunk<Consignment, number>(
  "consignments/fetchOne",
  async (id) => await getConsignment(id),
);

const consignmentSlice = createSlice({
  name: "consignments",
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<ConsignmentFilters>>) {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    setPage(state, action: PayloadAction<number>) {
      state.filters.page = action.payload;
    },
    clearFilters(state) {
      state.filters = {
        query: "",
        status: "",
        booking_type: "",
        customer_id: "",
        destination: "",
        page: 1,
        per_page: 10,
        first_mile_node_id: "",
        current_node_id: "",
        last_mile_node_id: "",
        created_from: "",
        created_to: "",
        updated_from: "",
        updated_to: "",
      };
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsignments.pending, (state) => {
        console.log("Redux: fetchConsignments pending");
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsignments.fulfilled, (state, action) => {
        console.log(
          "Redux: fetchConsignments fulfilled with payload:",
          action.payload,
        );
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = {
          current_page: action.payload.current_page,
          per_page: action.payload.per_page,
          total: action.payload.total,
        };
        console.log("Redux: Updated state:", {
          items: state.items,
          pagination: state.pagination,
        });
      })
      .addCase(fetchConsignments.rejected, (state, action) => {
        console.log(
          "Redux: fetchConsignments rejected with error:",
          action.error,
        );
        state.loading = false;
        state.error = action.error.message || "Failed to load consignments";
      })
      .addCase(fetchConsignment.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchConsignment.fulfilled, (state, action) => {
        state.selected = action.payload;
      })
      .addCase(fetchConsignment.rejected, (state, action) => {
        state.error = action.error.message || "Failed to load consignment";
      });
  },
});

export const { setFilters, setPage, clearFilters, clearError } =
  consignmentSlice.actions;
export default consignmentSlice.reducer;
